import { AIFraudOverviewResponse, AIFraudCasesResponse, DashboardOverviewResponse, SpecialtyAnalysis, ProviderAnalysis, FraudTrendMonth } from '../types/dashboard';
import { navigate } from '../utils/navigation';

const API_BASE_URL = process.env.REACT_APP_API_URL || (typeof window !== 'undefined' && window.location.port === '3000' ? `http://${window.location.hostname}:8000` : (typeof window !== 'undefined' ? window.location.origin : 'http://localhost:8000'));

class ApiService {
  private getAuthHeaders(): HeadersInit {
    const token = localStorage.getItem('fraudguard_token');
    return {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  }

  private handleUnauthorized() {
    localStorage.removeItem('fraudguard_token');
    localStorage.removeItem('fraudguard_user');
    navigate('/login');
  }

  private async fetchData<T>(url: string): Promise<T> {
    const response = await fetch(url, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });

    if (response.status === 401) {
      this.handleUnauthorized();
      throw new Error('Unauthorized: Please login again');
    }

    if (!response.ok) {
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  private async fetchDataWithProgress<T>(url: string, onProgress?: (percent: number) => void): Promise<T> {
    const token = localStorage.getItem('fraudguard_token');

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('GET', url);
      xhr.setRequestHeader('Content-Type', 'application/json');
      if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);

      xhr.onprogress = (event) => {
        if (event.lengthComputable && onProgress) {
          const percentVal = Math.round((event.loaded / event.total) * 100);
          onProgress(percentVal);
        } else if (onProgress) {
          onProgress(-1);
        }
      };

      xhr.onload = () => {
        if (xhr.status === 401) {
          this.handleUnauthorized();
          reject(new Error('Unauthorized: Please login again'));
          return;
        }
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            resolve(JSON.parse(xhr.responseText));
          } catch (e) {
            reject(new Error('Failed to parse response'));
          }
        } else {
          reject(new Error(`API Error: ${xhr.status} ${xhr.statusText}`));
        }
      };

      xhr.onerror = () => reject(new Error('Network error occurred.'));
      xhr.send();
    });
  }

  // AI Fraud: Overview
  async getAIFraudOverview(limit: number = 0, onProgress?: (p: number) => void): Promise<AIFraudOverviewResponse> {
    const url = `${API_BASE_URL}/api/fraud/ai-overview?limit=${limit}`;
    return this.fetchDataWithProgress<AIFraudOverviewResponse>(url, onProgress);
  }

  // AI Fraud: Cases
  async getAIFraudCases(minScore: number = 0.3, limit: number = 100000, onProgress?: (p: number) => void): Promise<AIFraudCasesResponse> {
    const url = `${API_BASE_URL}/api/fraud/ai-cases?min_score=${minScore}&limit=${limit}`;
    return this.fetchDataWithProgress<AIFraudCasesResponse>(url, onProgress);
  }

  // ── AI Fraud: Trend History (monthly) ────────────────────────────────────
  async getFraudTrendHistory(onProgress?: (p: number) => void): Promise<FraudTrendMonth[]> {
    const url = `${API_BASE_URL}/api/fraud/trend-history`;
    const response = await this.fetchDataWithProgress<{ months: FraudTrendMonth[] }>(url, onProgress);
    return response.months ?? [];
  }

  // Dashboard Overview
  async getDashboardOverview(onProgress?: (p: number) => void): Promise<DashboardOverviewResponse> {
    const url = `${API_BASE_URL}/api/dashboard/overview`;
    const data = await this.fetchDataWithProgress<any>(url, onProgress);
    return {
      totalClaims: data.total_claims ?? data.totalClaims ?? 0,
      totalAmount: data.total_amount ?? data.totalAmount ?? 0,
      avgAmount: data.avg_amount ?? data.avgAmount ?? 0,
      topSpecialty: data.top_specialty ?? data.topSpecialty ?? null,
      dateRange: data.date_range ?? data.dateRange ?? null,
    };
  }

  // Dashboard Overview Charts
  async getDashboardOverviewCharts(onProgress?: (p: number) => void): Promise<any> {
    const url = `${API_BASE_URL}/api/dashboard/overview-charts?limit=100000`;
    return this.fetchDataWithProgress<any>(url, onProgress);
  }

  // Specialties Data
  async getSpecialtiesData(limit: number = 20, onProgress?: (p: number) => void): Promise<SpecialtyAnalysis[]> {
    const url = `${API_BASE_URL}/api/dashboard/specialties?limit=${limit}`;
    const response = await this.fetchDataWithProgress<{ specialties: any[] }>(url, onProgress);
    return response.specialties.map(specialty => ({
      name: specialty.name,
      claims: specialty.claims,
      totalAmount: specialty.totalAmount,
      avgAmount: specialty.avgAmount,
      percentage: specialty.percentage
    }));
  }

  // Providers Data (Simple list)
  async getProvidersData(
    limit: number = 20,
    startDate?: string,
    endDate?: string
  ): Promise<ProviderAnalysis[]> {
    let url = `${API_BASE_URL}/api/dashboard/providers?limit=${limit}`;
    if (startDate) url += `&start_date=${startDate}`;
    if (endDate) url += `&end_date=${endDate}`;

    const response = await this.fetchData<{ providers: any[] }>(url);
    return response.providers.map(provider => ({
      name: provider.name,
      claims: provider.claims,
      totalAmount: provider.totalAmount,
      avgAmount: provider.avgAmount,
      percentage: provider.percentage
    }));
  }

  // Providers Data (Paginated & Filtered)
  async getProviders(queryString: string): Promise<any> {
    const url = `${API_BASE_URL}/api/dashboard/providers?${queryString}`;
    return this.fetchData<any>(url);
  }

  // ─── File Upload (Async) ──────────────────────────────────────────────────
  async uploadFile(file: File, onUploadProgress?: (percent: number) => void): Promise<{ task_id: string; filename: string; poll_url: string; status: string }> {
    const token = localStorage.getItem('fraudguard_token');

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${API_BASE_URL}/api/upload`);
      if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable && onUploadProgress) {
          const percentVal = Math.round((event.loaded / event.total) * 100);
          onUploadProgress(percentVal);
        }
      };

      xhr.onload = () => {
        if (xhr.status === 401) {
          this.handleUnauthorized();
          reject(new Error('Unauthorized: Please login again'));
          return;
        }
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            resolve(JSON.parse(xhr.responseText));
          } catch (e) {
            reject(new Error('Failed to parse response'));
          }
        } else {
          reject(new Error(`Upload Error: ${xhr.status} ${xhr.statusText}`));
        }
      };

      xhr.onerror = () => reject(new Error('Network error occurred during upload.'));

      const formData = new FormData();
      formData.append('file', file);
      xhr.send(formData);
    });
  }

  // ─── Polling ──────────────────────────────────────────────────────────────
  async getUploadStatus(taskId: string): Promise<{
    task_id: string;
    status: 'PENDING' | 'STARTED' | 'PROGRESS' | 'SUCCESS' | 'FAILURE';
    progress?: { stage: string; percent: number };
    result?: { filename: string; records_processed: number; fraud_summary: any };
    error?: string;
  }> {
    const url = `${API_BASE_URL}/api/upload/status/${taskId}`;
    return this.fetchData(url);
  }

  async pollUntilDone(
    taskId: string,
    onProgress?: (progress: { stage: string; percent: number }) => void,
    timerRef?: React.MutableRefObject<ReturnType<typeof setTimeout> | null>,
    intervalMs: number = 1000,
    maxAttempts: number = 120
  ): Promise<any> {
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      const statusData = await this.getUploadStatus(taskId);

      if (statusData.progress && onProgress) {
        const p = statusData.progress as any;
        const percent = p.percent !== undefined ? p.percent : p.progress;
        if (p.stage) {
          onProgress({ stage: p.stage, percent: percent ?? 0 });
        }
      }

      if (statusData.status === 'SUCCESS') {
        if (timerRef) timerRef.current = null;
        return statusData.result;
      }

      if (statusData.status === 'FAILURE') {
        if (timerRef) timerRef.current = null;
        throw new Error(statusData.error || 'Processing failed');
      }

      await new Promise<void>(resolve => {
        const timer = setTimeout(resolve, intervalMs);
        if (timerRef) timerRef.current = timer;
      });
    }

    throw new Error('Processing timed out. Please try again.');
  }

  // Health Check
  async healthCheck(): Promise<{ status: string; database: string; ai_models: string }> {
    const url = `${API_BASE_URL}/api/health`;
    return this.fetchData<{ status: string; database: string; ai_models: string }>(url);
  }

  // Queue Status
  async getQueueStatus(): Promise<{ active_workers: number; tasks_waiting: number; status: string }> {
    const url = `${API_BASE_URL}/api/upload/queue-status`;
    return this.fetchData<{ active_workers: number; tasks_waiting: number; status: string }>(url);
  }

  // ─── Forecasting ──────────────────────────────────────────────────────────
  async trainForecasting(providerName?: string): Promise<any> {
    const url = `${API_BASE_URL}/api/forecasting/train${providerName ? `?provider_name=${encodeURIComponent(providerName)}` : ''}`;
    const response = await fetch(url, {
      method: 'POST',
      headers: this.getAuthHeaders(),
    });
    if (!response.ok) throw new Error(`Train Error: ${response.status}`);
    return response.json();
  }

  async getForecastingResults(providerName?: string): Promise<any> {
    const url = `${API_BASE_URL}/api/forecasting/results${providerName ? `?provider_name=${encodeURIComponent(providerName)}` : ''}`;
    return this.fetchData<any>(url);
  }

  async getForecastingPerformance(providerName?: string): Promise<any> {
    const url = `${API_BASE_URL}/api/forecasting/performance${providerName ? `?provider_name=${encodeURIComponent(providerName)}` : ''}`;
    return this.fetchData<any>(url);
  }

  async getForecastingWeights(): Promise<any> {
    const url = `${API_BASE_URL}/api/forecasting/weights`;
    return this.fetchData<any>(url);
  }

  async getPolicies(): Promise<any[]> {
    const url = `${API_BASE_URL}/api/policies`;
    return this.fetchData<any[]>(url);
  }

  async getPolicyDetails(planName: string): Promise<any> {
    const url = `${API_BASE_URL}/api/policies/${encodeURIComponent(planName)}`;
    return this.fetchData<any>(url);
  }

  // ─── Bulk Policy PDF Upload (Sync) ─────────────────────────────────────────
  async uploadBulkPolicies(files: File[], onUploadProgress?: (percent: number) => void): Promise<{ task_id: string; status: string }> {
    const token = localStorage.getItem('fraudguard_token');

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${API_BASE_URL}/api/policies/upload-bulk`);
      if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable && onUploadProgress) {
          const percentVal = Math.round((event.loaded / event.total) * 100);
          onUploadProgress(percentVal);
        }
      };

      xhr.onload = () => {
        if (xhr.status === 401) {
          this.handleUnauthorized();
          reject(new Error('Unauthorized: Please login again'));
          return;
        }
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            resolve(JSON.parse(xhr.responseText));
          } catch (e) {
            reject(new Error('Failed to parse response'));
          }
        } else {
          reject(new Error(`Upload Error: ${xhr.status} ${xhr.statusText}`));
        }
      };

      xhr.onerror = () => reject(new Error('Network error occurred during upload.'));

      const formData = new FormData();
      for (let i = 0; i < files.length; i++) {
        formData.append('files', files[i]);
      }
      xhr.send(formData);
    });
  }

  // ── AI Chatbot (Groq) ──────────────────────────────────────────────────
  async sendChatMessage(messages: { role: string; content: string }[], includeSystemStats: boolean = true): Promise<{ reply: string; model: string; status: string }> {
    const url = `${API_BASE_URL}/api/chat`;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 25000);

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        signal: controller.signal,
        body: JSON.stringify({
          messages,
          include_system_stats: includeSystemStats,
        }),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({ detail: 'Network error' }));
        throw new Error(err.detail || `Chatbot Error: ${response.status}`);
      }

      return await response.json();
    } catch (err: any) {
      if (err.name === 'AbortError') {
        throw new Error('Chatbot request timed out after 25s. The server is taking too long to respond.');
      }
      throw err;
    } finally {
      clearTimeout(timeoutId);
    }
  }

  async getChatbotHealth(): Promise<{ status: string; model?: string }> {
    const url = `${API_BASE_URL}/api/chat/health`;
    return this.fetchData<{ status: string; model?: string }>(url);
  }

  async getQuickPrompts(): Promise<{ prompts: Array<{ id: string; icon: string; label: string; query: string }> }> {
    const url = `${API_BASE_URL}/api/chat/quick-prompts`;
    return this.fetchData<{ prompts: Array<{ id: string; icon: string; label: string; query: string }> }>(url);
  }

  async submitChatFeedback(data: { message_id?: string; rating: 'up' | 'down'; reasons?: string[]; comment?: string }): Promise<{ status: string; message: string }> {
    const url = `${API_BASE_URL}/api/chat/feedback`;
    const response = await fetch(url, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      throw new Error(`Failed to submit feedback (${response.status})`);
    }
    return response.json();
  }

  // ── Produced Fraud Reports (Physicians & Providers) ───────────────────────
  async getLatestFraudReport(): Promise<any> {
    const url = `${API_BASE_URL}/api/fraud/report/latest`;
    return this.fetchData<any>(url);
  }

  async getPhysicianFraudReport(physicianName: string): Promise<any> {
    const encoded = encodeURIComponent(physicianName);
    const url = `${API_BASE_URL}/api/fraud/report/physician/${encoded}`;
    return this.fetchData<any>(url);
  }

  async getProviderFraudReport(providerName: string): Promise<any> {
    const encoded = encodeURIComponent(providerName);
    const url = `${API_BASE_URL}/api/fraud/report/provider/${encoded}`;
    return this.fetchData<any>(url);
  }

  async getPhysicianClaims(physicianName: string, page: number = 1, pageSize: number = 20): Promise<any> {
    const encoded = encodeURIComponent(physicianName);
    const url = `${API_BASE_URL}/api/claims/filter?treating_physician=${encoded}&page=${page}&pageSize=${pageSize}`;
    return this.fetchData<any>(url);
  }

  // ── Physician Benchmarking ────────────────────────────────────────────────
  async getPhysiciansBenchmarkDirectory(): Promise<{ status: string; total_physicians: number; physicians: any[] }> {
    const url = `${API_BASE_URL}/api/reports/physicians/benchmark/physicians-list`;
    return this.fetchData<any>(url);
  }

  async getSpecialtyTaxonomies(): Promise<{ status: string; taxonomies: any[] }> {
    const url = `${API_BASE_URL}/api/reports/physicians/benchmark/taxonomies`;
    return this.fetchData<any>(url);
  }

  async getFacilitiesList(): Promise<{ status: string; facilities: string[] }> {
    const url = `${API_BASE_URL}/api/reports/physicians/benchmark/facilities`;
    return this.fetchData<any>(url);
  }

  async getPhysicianBenchmark(
    physicianName: string,
    params?: {
      specialty?: string;
      sub_specialty?: string;
      date_from?: string;
      date_to?: string;
      facility?: string;
      patient_type?: string;
      min_peers?: number;
    }
  ): Promise<any> {
    const encoded = encodeURIComponent(physicianName);
    let url = `${API_BASE_URL}/api/reports/physicians/${encoded}/benchmark`;
    const query = new URLSearchParams();
    if (params?.specialty) query.append('specialty', params.specialty);
    if (params?.sub_specialty) query.append('sub_specialty', params.sub_specialty);
    if (params?.date_from) query.append('date_from', params.date_from);
    if (params?.date_to) query.append('date_to', params.date_to);
    if (params?.facility && params.facility !== 'all') query.append('facility', params.facility);
    if (params?.patient_type && params.patient_type !== 'all') query.append('patient_type', params.patient_type);
    if (params?.min_peers !== undefined) query.append('min_peers', params.min_peers.toString());
    
    const queryString = query.toString();
    if (queryString) url += `?${queryString}`;
    return this.fetchData<any>(url);
  }

  async getPhysicianDrilldown(
    physicianName: string,
    params?: {
      metric_type?: string;
      metric_value?: string;
      date_from?: string;
      date_to?: string;
      facility?: string;
      page?: number;
      page_size?: number;
    }
  ): Promise<any> {
    const encoded = encodeURIComponent(physicianName);
    let url = `${API_BASE_URL}/api/reports/physicians/${encoded}/benchmark/drilldown`;
    const query = new URLSearchParams();
    if (params?.metric_type) query.append('metric_type', params.metric_type);
    if (params?.metric_value) query.append('metric_value', params.metric_value);
    if (params?.date_from) query.append('date_from', params.date_from);
    if (params?.date_to) query.append('date_to', params.date_to);
    if (params?.facility && params.facility !== 'all') query.append('facility', params.facility);
    if (params?.page) query.append('page', params.page.toString());
    if (params?.page_size) query.append('page_size', params.page_size.toString());

    const queryString = query.toString();
    if (queryString) url += `?${queryString}`;
    return this.fetchData<any>(url);
  }
}

export const apiService = new ApiService();
export default apiService;